'use client';

import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Switch — accessible on/off toggle built on @radix-ui/react-switch.
 *
 * Radix provides `role="switch"`, `aria-checked`, and the ARIA switch
 * keyboard pattern (Space toggles; Enter does NOT activate) natively, so
 * consumers should NOT pass those props themselves. Pair the Switch with a
 * visible label via `aria-labelledby` (or `aria-label` for icon-only cases).
 *
 * State-change handler: `onCheckedChange={(checked: boolean) => ...}`.
 * The legacy `onChange={(e) => e.target.checked}` shape from the previous
 * <input type="checkbox"> implementation is gone — Radix Switch is a
 * <button>, not an <input>, so consumers must use `onCheckedChange`.
 *
 * Touch target (WCAG 2.5.5 / #5301): the visual track stays 20×40 px
 * (`h-5 w-10`) for chrome density, but the *interactive* hit area is
 * extended to 44×44 px via a transparent `::before` pseudo-element — the
 * same technique as `HIT_AREA_44` in `lib/styles/touch-targets.ts`,
 * inlined here so every Switch site inherits the compliant target with no
 * per-call opt-in. Math: width 40 + 2×2 = 44 (`before:inset-x-[-2px]`);
 * height 20 + 2×12 = 44 (`before:inset-y-[-12px]`). The `switch.test.tsx`
 * source fence pins these fragments so the expansion can't be dropped.
 * Adjacent-target note: the 12 px vertical extension means stacked
 * Switches need ≥ 24 px center-to-center distance (between switch tracks)
 * or their hit areas overlap (WCAG 2.5.5 disallows overlap between distinct
 * controls); all existing sites clear this comfortably (e.g. OfficeHoursPicker
 * closed rows: space-y-2 + 32 px row = 40 px center-to-center).
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      'peer relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center rounded-full border border-line-default transition-colors',
      "before:absolute before:inset-x-[-2px] before:inset-y-[-12px] before:content-['']",
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tertiary focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'data-[state=checked]:bg-brand-secondary-color dark:data-[state=checked]:bg-brand-secondary-dark data-[state=checked]:border-transparent',
      'data-[state=unchecked]:bg-surface-muted',
      className
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        'pointer-events-none block h-4 w-4 rounded-full bg-surface-card border border-line-default dark:border-transparent ring-0 transition-transform',
        'translate-x-[2px] data-[state=checked]:translate-x-[20px]',
        'data-[state=checked]:border-content-inverse'
      )}
    />
  </SwitchPrimitives.Root>
));

Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
